import ErrorService from "@/services/common/ErrorService";

import {
	BuildKinematicConfiguration,
	DownloadFile,
	NormalizeConfiguration
} from "../service/ConfigurationService";
import {
	ADD_GEAR,
	ADD_GEAR_MESH_FREQUENCY,
	ADD_GEARBOX_STAGE,
	ADD_SHAFT_BEARING,
	ADD_TURBINE_SHAFTS,
	CREATE_KINEMATIC_CONFIGURATION,
	DOWNLOAD_KINEMATIC_CONFIGURATION,
	GET_KINEMATIC_CONFIGURATION,
	REMOVE_GEAR,
	REMOVE_GEAR_MESH_FREQUENCY,
	REMOVE_GEARBOX_STAGE,
	REMOVE_TURBINE_SHAFTS,
	SET_GEAR,
	SET_GEAR_MESH_FREQUENCY,
	SET_GEARBOX_MODEL,
	SET_GEARBOX_STAGES,
	SET_NEW_CONFIGURATION,
	SET_TURBINE_PROPERTIES,
	SET_TURBINE_PROPERTIES_VALIDITY,
	SET_GEAR_MESH_FREQUENCIES_VALIDITY,
	SET_EFFECTIVE_SINCE_VALIDITY,
	SET_SHAFTS_VALIDITY,
	SET_GEARBOX_VALIDITY,
	SET_GEARS_VALIDITY,
	SET_TURBINE_SHAFTS,
	SET_TURBINE_VERSIONS,
	SHOW_TOAST_MESSAGE,
	ENABLE_FORMS,
	SET_SHAFTS_BEARING_VALIDITY
} from "./KinematicConfiguratorActionsType";
import ToastMessageType from "@/constants/enums/ToastMessageType";
import KinematicParametersService from "@/services/wind/KinematicParametersService";
import { RESET_STATE } from "@/stores/CommumActionTypes";
import ObjectHandleService from "@/services/common/ObjectHandleService";

const actions = {
	/**
	 * Define as propriedades da turbina.
	 * @param {Object} turbineProperties - Objeto com propriedades da turbina.
	 */
	[SET_TURBINE_PROPERTIES]({ commit }, turbineProperties)
	{
		commit(SET_TURBINE_PROPERTIES, turbineProperties);
	},

	/**
	 * Atualiza booleano de validação para as propridedades da turbina.
	 * @param {boolean} isValid
	 */
	[SET_TURBINE_PROPERTIES_VALIDITY]({ commit }, isValid)
	{
		commit(SET_TURBINE_PROPERTIES_VALIDITY, isValid);
	},

	/**
	 * Atualiza booleano de validação para a gearbox.
	 * @param {boolean} isValid
	 */
	[SET_GEARBOX_VALIDITY]({ commit }, isValid)
	{
		commit(SET_GEARBOX_VALIDITY, isValid);
	},

	/**
	 * Atualiza booleano de validação para dos eixos.
	 * @param {boolean} isValid
	 */
	[SET_SHAFTS_VALIDITY]({ commit }, isValid)
	{
		commit(SET_SHAFTS_VALIDITY, isValid);
	},

	/**
	 * Atualiza booleano de validação para os rolamentos do eixo.
	 * @param {boolean} isValid
	 */
	[SET_SHAFTS_BEARING_VALIDITY]({ commit }, isValid)
	{
		commit(SET_SHAFTS_BEARING_VALIDITY, isValid);
	},

	/**
	 * Atualiza booleano de validação para de engrenagens.
	 * @param {boolean} isValid
	 */
	[SET_GEARS_VALIDITY]({ commit }, isValid)
	{
		commit(SET_GEARS_VALIDITY, isValid);
	},

	/**
	 * Atualiza booleano de validação para de frequência de engrenamento.
	 * @param {boolean} isValid
	 */
	[SET_GEAR_MESH_FREQUENCIES_VALIDITY]({ commit }, isValid)
	{
		commit(SET_GEAR_MESH_FREQUENCIES_VALIDITY, isValid);
	},

	/**
	 * Atualiza booleano de validação para da data de vigência.
	 * @param {boolean} isValid
	 */
	[SET_EFFECTIVE_SINCE_VALIDITY]({ commit }, isValid)
	{
		commit(SET_EFFECTIVE_SINCE_VALIDITY, isValid);
	},

	/**
	 * Define o modelo da caixa de engrenagem.
	 * @param {Object} gearboxModel - Modelo da caixa de engrenagem.
	 */
	[SET_GEARBOX_MODEL]({ commit }, gearboxModel)
	{
		commit(SET_GEARBOX_MODEL, gearboxModel);
	},

	/**
	 * Adiciona um estágio à caixa de engrenagens.
	 * @param {Object} gearboxStageModel - Objeto representando o estágio da caixa.
	 */
	[ADD_GEARBOX_STAGE]({ commit, state }, gearboxStageModel)
	{
		gearboxStageModel.id = state.gearboxStages.length;
		commit(ADD_GEARBOX_STAGE, gearboxStageModel);
	},

	/**
	 * Remove um estágio da caixa de engrenagens.
	 * @param {number} stageId - ID do estágio a ser removido.
	 */
	[REMOVE_GEARBOX_STAGE]({ commit }, stageId)
	{
		commit(REMOVE_GEARBOX_STAGE, stageId);
	},

	/**
	 * Adiciona um eixo à turbina.
	 * @param {Object} shaftModel - Objeto representando o eixo da turbina.
	 */
	[ADD_TURBINE_SHAFTS]({ commit, state }, shaftModel)
	{
		shaftModel.id = state.shafts.length;
		commit(ADD_TURBINE_SHAFTS, shaftModel);
	},

	/**
	 * Define os eixos da turbina.
	 * @param {Array} shaftsModel - Lista de eixos da turbina.
	 */
	[SET_TURBINE_SHAFTS]({ commit }, shaftsModel)
	{
		const shaftsWithIds = shaftsModel.map((shaft, index) => ({
			...shaft,
			id: shaft.id ?? index
		}));
		commit(SET_TURBINE_SHAFTS, shaftsWithIds);
	},

	/**
	 * Remove eixo da turbina.
	 * @param {number} shaftId - ID do eixo a ser removido.
	 */
	[REMOVE_TURBINE_SHAFTS]({ commit }, shaftId)
	{
		commit(REMOVE_TURBINE_SHAFTS, shaftId);
	},

	/**
	 * Adiciona um mancal ao eixo.
	 * @param {Object} bearingModel - Objeto representando o mancal.
	 */
	[ADD_SHAFT_BEARING]({ commit }, bearingModel)
	{
		commit(ADD_SHAFT_BEARING, bearingModel);
	},

	/**
	 * Define os estágios da caixa de engrenagens.
	 * @param {Array} gearboxStages - Lista de estágios da caixa.
	 */
	[SET_GEARBOX_STAGES]({ commit }, gearboxStages)
	{
		const stagesWithIds = gearboxStages.map((stage, index) => ({
			...stage,
			id: stage.id ?? index
		}));
		commit(SET_GEARBOX_STAGES, stagesWithIds);
	},

	/**
	 * Adiciona uma engrenagem.
	 * @param {Object} gear - Objeto representando a engrenagem.
	 */
	[ADD_GEAR]({ commit }, gear)
	{
		commit(ADD_GEAR, gear);
	},

	/**
	 * Remove uma engrenagem.
	 * @param {number} gearId - ID da engrenagem a ser removida.
	 */
	[REMOVE_GEAR]({ commit }, gearId)
	{
		commit(REMOVE_GEAR, gearId);
	},

	/**
	 * Define as engrenagens.
	 * @param {Array} gears - Lista de engrenagens.
	 */
	[SET_GEAR]({ commit }, gears)
	{
		commit(SET_GEAR, gears);
	},

	/**
	 * Define frequências de engrenamento.
	 * @param {Array} gearMeshFrequencies - Lista de frequências de engrenamento.
	 */
	[SET_GEAR_MESH_FREQUENCY]({ commit }, gearMeshFrequencies)
	{
		commit(SET_GEAR_MESH_FREQUENCY, gearMeshFrequencies);
	},

	/**
	 * Habilita os formulários dos componentes da turbina.
	 */
	[ENABLE_FORMS]({ commit }, isDisabled)
	{
		commit(ENABLE_FORMS, isDisabled);
	},

	/**
	 * Adiciona frequência de engrenamento.
	 * @param {Object} frequency - Objeto representando a frequência.
	 */
	[ADD_GEAR_MESH_FREQUENCY]({ commit }, frequency)
	{
		commit(ADD_GEAR_MESH_FREQUENCY, frequency);
	},

	/**
	 * Remove frequência de engrenamento.
	 * @param {number} frequencyId - ID da frequência a ser removida.
	 */
	[REMOVE_GEAR_MESH_FREQUENCY]({ commit }, frequencyId)
	{
		commit(REMOVE_GEAR_MESH_FREQUENCY, frequencyId);
	},

	/**
	 * Busca a configuração cinemática para um determinado elemento de monitoramento.
	 * @param {Object} configInfo - Deve conter `MonitoringElementId`.
	 */
	async [GET_KINEMATIC_CONFIGURATION]({ dispatch, commit }, configInfo)
	{
		if (!configInfo?.monitoringElementId) return;

		commit(SET_TURBINE_PROPERTIES, configInfo);

		const newConfiguration = await KinematicParametersService.GetKinematicConfiguration(configInfo.monitoringElementId);

		if (ObjectHandleService.IsEmpty(newConfiguration))
		{
			dispatch(RESET_STATE);
		}
		else
		{
			dispatch(SET_NEW_CONFIGURATION, newConfiguration);
		}
	},

	/**
	 * Define uma nova configuração cinemática com base na resposta da API.
	 * @param {Object} newConfiguration - Objeto com a nova configuração completa.
	 */
	SET_NEW_CONFIGURATION({ commit }, newConfiguration)
	{
		const normalizedConfiguration = NormalizeConfiguration(newConfiguration);

		commit(SET_TURBINE_PROPERTIES, normalizedConfiguration.turbineProperties);
		commit(SET_GEARBOX_MODEL, normalizedConfiguration.gearbox);
		commit(SET_TURBINE_SHAFTS, normalizedConfiguration.shaftsModel);
		commit(SET_GEARBOX_STAGES, normalizedConfiguration.gearboxStages);
		commit(SET_GEAR, normalizedConfiguration.gears);
		commit(SET_GEAR_MESH_FREQUENCY, normalizedConfiguration.gearsMeshFrequencies);
		commit(SET_TURBINE_VERSIONS, normalizedConfiguration.turbineVersion);
	},

	/**
	 * Cria uma configuração cinemática com base no estado atual.
	 * @param {Object} configParams - Parâmetros adicionais para criação da configuração.
	 */
	async [CREATE_KINEMATIC_CONFIGURATION]({ dispatch, state }, monitoringElementId)
	{
		try
		{
			const stateConfiguration = state;

			stateConfiguration.turbineProperties.monitoringElementId = monitoringElementId;
			const configurationToCreate = await BuildKinematicConfiguration(stateConfiguration);
			await KinematicParametersService.CreateKinematicConfiguration(configurationToCreate);
			dispatch(SHOW_TOAST_MESSAGE,
				 { message: "A configuração foi salva com sucesso!", type: ToastMessageType.SUCCESS });
		}
		catch (e)
		{
			dispatch(SHOW_TOAST_MESSAGE, "Houve um problema ao salvar a configuração!");
		}
	},

	/**
	 * Define as versões da turbina.
	 * @param {Array} turbineVersions - Lista de versões da turbina.
	 */
	[SET_TURBINE_VERSIONS]({ commit }, turbineVersions)
	{
		commit(SET_TURBINE_VERSIONS, turbineVersions);
	},

	/**
	 * Faz download da configuração cinemática atual em formato JSON.
	 */
	async [DOWNLOAD_KINEMATIC_CONFIGURATION]({
		state, rootGetters, dispatch
	})
	{
		try
		{
			const configuration = await BuildKinematicConfiguration(state, true);
			const FileJson = JSON.stringify(configuration, null, 2);

			const blobConfiguration = new Blob([ FileJson ], { type: "application/json" });

			const turbines = rootGetters["WindAssetModule/getTurbines"];

			const selectedId = state.turbineProperties.monitoringElementId;
			const selectedTurbine = turbines.find(t => t.id === selectedId);
			const turbineName = selectedTurbine?.name || "turbine";

			const now = new Date();
			const yyyy = now.getFullYear();
			const mm = String(now.getMonth() + 1).padStart(2, "0");
			const dd = String(now.getDate()).padStart(2, "0");

			const fileName = `${turbineName}-${yyyy}-${mm}-${dd}.json`;

			DownloadFile(blobConfiguration, fileName);
		}
		catch (e)
		{
			const error = ErrorService.HandleError(e);
			dispatch(SHOW_TOAST_MESSAGE, { message: "Não foi possível realizar o download!" });
		}
	},

	/**
	 * Exibe uma mensagem na tela.
	 * @param {String} message Mensagem a ser exibida.
	 * @param {number} type Tipo da mensagem a ser exibida.
	 */
	[SHOW_TOAST_MESSAGE]({ commit }, { message = "", type = ToastMessageType.ERROR })
	{
		commit(SHOW_TOAST_MESSAGE, { message, type });
	},

	/**
	 * Reseta o estado.
	 */
	[RESET_STATE]({ commit })
	{
		commit(RESET_STATE);
	},

};

export default { actions };
